import type { RoadmapStep, WorldNode } from "./types";

/**
 * Everything in this scene is measured in metres, in a right-handed world where
 * the road runs toward -Z. The camera drives along a curve; checkpoints stand on
 * the verge beside it.
 *
 * This module deliberately imports nothing from three.js — it is pulled into the
 * eager bundle by the scroll hook, and the WebGL runtime must stay in the lazy
 * scene chunk. Curve geometry lives in `scene/roadPath.ts`.
 */
export const WORLD = {
  /** Metres of road between two consecutive checkpoints. */
  nodeSpacing: 27,
  /** Half-width of the driveable asphalt. */
  roadHalf: 4.3,
  /** Grass-to-asphalt transition strip on each side. */
  shoulderWidth: 1.5,
  /**
   * Camera height above the road surface. Chest-height would bury the frame in
   * asphalt; riding a little above the roof line keeps both the road ribbon and
   * the markers standing along it in view.
   */
  eyeHeight: 6.4,
  /** How far up the road the camera aims. Larger = you see more of the bend. */
  lookAhead: 30,
  /** Height of the aim point above the road — the difference sets the pitch. */
  lookHeight: 1.7,
  /** Empty runway before the first checkpoint, and from the last one to the arch. */
  leadIn: 26,
  runOut: 64,
  /**
   * Road and ground built past the finish arch. Nobody drives it — it exists so
   * the far edge of the world sits deep in the fog instead of ending in a cliff
   * of sky the moment you reach the end.
   */
  tail: 240,
  /** Lateral offset where checkpoint markers stand, from the road centre. */
  markerLateral: 5.9,
  /** How far short of a checkpoint the camera parks when resuming or jumping. */
  approach: 20,
  /**
   * How close you get before the label moves on to the next checkpoint. Inside
   * this radius a verge marker is beside the lens, so its card would sit off the
   * frame — the marker keeps its 3D presence, the words go on ahead.
   */
  handover: 11,
  /** Distance between road control points — sets how lazy the meander feels. */
  controlSpacing: 38,
  fogNear: 60,
  fogFar: 260,
  /** Checkpoints closer than this get an HTML card. */
  cardRange: 74,
  /** Markers beyond this are not worth mounting. */
  drawRange: 300,
} as const;

export function clamp(n: number, min: number, max: number): number {
  return n < min ? min : n > max ? max : n;
}

/** Deterministic pseudo-random in [0,1) — same road on every render and device. */
export function rand(seed: number): number {
  const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

/**
 * Lay the steps out along the road. `start`/`finish` are gateways rather than
 * stops, so they are excluded here and drawn at either end of the road.
 */
export function buildWorldNodes(steps: RoadmapStep[]): WorldNode[] {
  const nodes: WorldNode[] = [];
  let roadIndex = 0;
  let leafIdx = 0;

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    if (!step || step.kind === "start" || step.kind === "finish") continue;

    const side: WorldNode["side"] =
      step.kind === "phase" ? "center" : leafIdx % 2 === 0 ? "left" : "right";
    if (step.kind === "checkpoint") leafIdx += 1;

    nodes.push({
      step,
      index: i,
      roadIndex,
      distance: WORLD.leadIn + roadIndex * WORLD.nodeSpacing,
      side,
    });
    roadIndex += 1;
  }
  return nodes;
}

export function lastNodeDistance(nodes: WorldNode[]): number {
  const last = nodes[nodes.length - 1];
  return last ? last.distance : WORLD.leadIn;
}

/** Where the chequered arch stands: a run-out past the final checkpoint. */
export function finishDistance(nodes: WorldNode[]): number {
  return lastNodeDistance(nodes) + WORLD.runOut;
}

/** Total geometry built, arch plus the tail that keeps the world's edge hidden. */
export function roadLength(nodes: WorldNode[]): number {
  return finishDistance(nodes) + WORLD.tail;
}

/**
 * The stretch of road the scrollbar maps onto: it opens a little before the first
 * checkpoint and closes with the arch filling the windscreen. Stopping short of
 * the arch would leave the journey unfinished; driving through it would put the
 * end of the built world in shot.
 */
function driveRange(nodes: WorldNode[]): { start: number; span: number } {
  const start = WORLD.leadIn - WORLD.approach;
  const end = finishDistance(nodes) - 16;
  return { start, span: Math.max(end - start, 1) };
}

export function distanceForProgress(progress: number, nodes: WorldNode[]): number {
  const { start, span } = driveRange(nodes);
  return start + clamp(progress, 0, 1) * span;
}

export function progressForDistance(distance: number, nodes: WorldNode[]): number {
  const { start, span } = driveRange(nodes);
  return clamp((distance - start) / span, 0, 1);
}

/** Progress that parks the camera on approach to `node`, not on top of it. */
export function progressForNode(node: WorldNode | undefined, nodes: WorldNode[]): number {
  if (!node) return 0;
  return progressForDistance(node.distance - WORLD.approach, nodes);
}

/**
 * Index into `nodes` of the checkpoint the camera is *approaching*. Rounding to
 * the merely closest node would hand focus to markers already behind the lens,
 * whose cards cannot be drawn at all; handing over exactly on passing leaves the
 * label off-frame for the last few metres. So focus runs one `handover` ahead,
 * which keeps the named checkpoint between roughly 11 and 38 metres out — near
 * enough to read, far enough to stay in shot.
 */
export function nodeIndexAtDistance(nodes: WorldNode[], distance: number): number {
  if (nodes.length === 0) return -1;
  const raw = Math.ceil((distance - WORLD.leadIn + WORLD.handover) / WORLD.nodeSpacing);
  return clamp(raw, 0, nodes.length - 1);
}
